import React, { useMemo, useRef } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCIcon, RCCInput, RCCSwitch, RCCTextArea, RCCTooltip } from "react-common-components";
import { useWatch } from "react-hook-form";
import LovUsers from "src/components/Lov/Users";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import  ModalSelectUsers from "src/components/SelectUsers";
import { IRefModalSelectUsers , ISelectedEftba005 } from "src/components/SelectUsers/interface";
import { FormMeetingAction, IMeetingParticipant } from "../../../interface";
import { IParticipantTab } from "../interface";


const ParticipantsTab: React.FC<IParticipantTab> = ({
	participantControls,
	control,
	errors,
	register,
	clearErrors,
	setValue,
	formAction,
	responsible
}) => {
	
	const modalSelectUsersRef = useRef<IRefModalSelectUsers>(null);
	
	
	const {user,administrator,superUser} = useAuth();
	const { translate } = useTranslation();
	const showComplete= 
		formAction !== FormMeetingAction.ADD && 
		formAction !== FormMeetingAction.CHANGE && 
		formAction !== FormMeetingAction.REOPEN;
								
	const isDisabled = (userId?:string) => {
		
		if(!showComplete)
			return false;

		if(formAction === FormMeetingAction.VIEW)
			return true;

		return formAction === FormMeetingAction.EXECUTE && 
			(user.efa005Id.toString() !== userId && !administrator && !superUser && responsible !== userId);
	};

	const participantWatch = useWatch({
		control,
		name: "participants"
	});

	const controlledFields = participantControls.fields.map((field, index) => ({
		...field,
		...participantWatch && participantWatch[index]
 	 }));

	const addNewItem = (typeParticipant: "user"|"person") => {
		clearErrors("participants");
        
		const newItem = {
			id: v4(),
			ara002Id:0,
			ara002Efa005Id: typeParticipant === "user" ? "" : undefined,
			ara002Presente:"N",
			ara002Acordoata:"N",
			ara002Comentario:"",
			ara002Participante: "",
			typeParticipant
		} as IMeetingParticipant;

		participantControls.append(newItem);
	};

	const handleRemoveItem = (index: number) => {
		clearErrors("participants");
		participantControls.remove(index);
	};

	const returnError = () =>
	{
		const err = errors.participants as any;

		return err?.message;
	};

	const participant = (pt:IMeetingParticipant, index:number) => (
		<RCCGrid
			key={pt.id}
			container
			item
			alignItems="flex-end"
			justifyContent="space-between"
		>
			<input 
				{...register(`participants.${index}.typeParticipant`)} 
				name={`participants.${index}.typeParticipant`} 
				type="hidden"
				value={pt.typeParticipant}
				readOnly
			/>
			<RCCGrid
				container
				item
				xs={10}
			>
				{pt.typeParticipant === "user" ?
					<>
						<LovUsers 
							control={control}
							label={translate("Usuário")}
							name={`participants.${index}.ara002Efa005Id`}
							defaultValue={pt.ara002Efa005Id?.toString()}
							error={errors.participants && errors.participants[index]?.ara002Efa005Id}
							required
							disabled={isDisabled(pt.ara002Efa005Id)}
							onChangeCapture={() => clearErrors("participants")}
						/>
						<input 
							{...register(`participants.${index}.ara002Participante`)} 
							name={`participants.${index}.ara002Participante`} 
							type="hidden"
							defaultValue={pt.ara002Participante}
							readOnly
						/>
					</>
					:
					<>
						<RCCInput 
							register={register}
							name={`participants.${index}.ara002Participante`} 
							label={translate("Nome")}
							defaultValue={pt.ara002Participante}
							maxLength={200}
							disabled={isDisabled(pt.ara002Efa005Id) || (showComplete && pt.ara002Id !== 0)}
							onBlurCapture={() => clearErrors("participants")}
							error={errors.participants && errors.participants[index]?.ara002Participante}
							required
						/>
						<input 
							{...register(`participants.${index}.ara002Efa005Id`)} 
							name={`participants.${index}.ara002Efa005Id`} 
							type="hidden"
							defaultValue={pt.ara002Efa005Id}
							readOnly
						/>
					</>
				}
			</RCCGrid>
			{(!isDisabled(pt.ara002Efa005Id) ||(!showComplete && pt.ara002Id !== 0))&& 
				<RCCTooltip
					color="red"
					text={translate("Remover")}
				>
					<RCCButton 
						color="red"
						icon={{
							name: "trash"
						}}
						onClick={() => handleRemoveItem(index)}
						disabled={isDisabled()}
					/>
				</RCCTooltip>
			}
			{showComplete && 
				<RCCTooltip
					color={pt.showExtra ? "blue" : "grey"}
					text={translate("Detalhes")}
				>
					<RCCButton 
						color={pt.showExtra ? "blue" : "grey"}
						icon={{
							name: pt.showExtra ? "eye" : "eyeSlash"
						}}
						onClick={() => setValue(`participants.${index}.showExtra` as "participants.0.showExtra",!pt.showExtra)}
					/>
				</RCCTooltip>
			}
			{pt.showExtra &&
				<RCCGrid container >
					<RCCGrid container> 
						<RCCSwitch
							name={`participants.${index}.ara002Presente`}
							register={register}
							label={translate("Esteve Presente")}
							defaultChecked={pt.ara002Presente === "S"}
							disabled={isDisabled(pt.ara002Efa005Id)}
						/>
						<RCCSwitch
							name={`participants.${index}.ara002Acordoata`}
							register={register}
							label={`${translate("Concorda com a ata")}?`}
							defaultChecked={pt.ara002Acordoata === "S"}
							disabled={isDisabled(pt.ara002Efa005Id)}
						/>
					</RCCGrid>
					<RCCGrid container> 
						<RCCTextArea
							name={`participants.${index}.ara002Comentario`}
							register={register}
							disabled={isDisabled(pt.ara002Efa005Id)}
							label={translate("Comentário")}
							defaultValue={pt.ara002Comentario}
							maxLength={4000}
						/>
					</RCCGrid>
				</RCCGrid>
			}
		</RCCGrid>
	);

	const ListParticipantsMemo = useMemo(() => (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
				justifyContent="center"
			>
				{controlledFields.some((particiant) => particiant.typeParticipant === "user") && (
					<RCCGrid
						container
						item
						xs={12}
						md={10}
					>
						<Title color="purple"
							size="sm"
						>
							{translate("Participantes")}
						</Title>
						<hr />
					</RCCGrid>
				)}
				<RCCGrid
					container
					item
					xs={12}
					md={10}
				>
					{controlledFields.map((particiant, index) => particiant.typeParticipant === "user"  && 
						participant(particiant,index)
					)}
				</RCCGrid>
			</RCCGrid>
			{/* Livres */}
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
				justifyContent="center"
			>
				{controlledFields.some((person) => person.typeParticipant === "person") && (
					<RCCGrid
						container
						item
						xs={12}
						md={10}
					>
						<Title color="purple"
							size="sm"
						>
							{translate("Participante externo")}
						</Title>
						<hr />
					</RCCGrid>
				)}
				<RCCGrid
					container
					item
					xs={12}
					md={10}
				>
					{controlledFields.map((person, index) => person.typeParticipant === "person" && 
						participant(person,index)
					)}
				</RCCGrid>
			</RCCGrid>
		</>
	),[controlledFields]);

	const callbackSelectUsers = (listOfUsers: ISelectedEftba005[] | undefined) => {
		if(!listOfUsers)
			return;
		const hasRegisterUsers = participantControls.fields?.map((e) => Number(e.ara002Efa005Id));	
		const newUsers: any = listOfUsers?.filter((e) => !hasRegisterUsers.includes(e.efa005Id)).map((userX) => ({
			ara002Efa005Id: userX.efa005Id,
			ara002Participante: userX.efa005Nome,
			typeParticipant: "user"
		}));
		participantControls.append(newUsers);
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
			>          
				<RCCGrid 
					container
					item
				>
					<RCCGrid
						container
						item
						xs={6}
						alignContent="center"
					>
						{!isDisabled() && 
						<>
							<RCCTooltip
								color="purple"
								text={translate("Adicionar usuário")}
							>
								<RCCButton
									color="purple"
									icon={{
										name: "plus"
									}}
									onClick={() => addNewItem("user")}
								>
									{translate("Usuário")}
								</RCCButton>
							</RCCTooltip>
							<RCCTooltip
								color="blue"
								text={translate("Adicionar grupo")}
							>
								<RCCButton
									color="blue"
									icon={{
										name: "plus"
									}}
									onClick={() => {	
										modalSelectUsersRef.current?.openModal(callbackSelectUsers);
									}}
								>
									{translate("Adicionar usuários por grupo")}
								</RCCButton>
							</RCCTooltip>
							<RCCTooltip
								color="grey"
								text={translate("Adicionar participante externo")}
							>
								<RCCButton
									color="grey"
									icon={{
										name: "plus"
									}}
									onClick={() => addNewItem("person")}
								>
									{translate("Participante externo")}
								</RCCButton>
							</RCCTooltip>
						</>
						}
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={6}
						alignContent="center"
						alignItems="center"
						justifyContent="flex-end"
					>
						{errors.participants && (
							<>
								<RCCIcon name="infoCircle" size={18} color="var(--red)"/>
								<Title color="red" size="xs">{returnError()}</Title>
							</>
						)}
					</RCCGrid>
				</RCCGrid>
				{
					controlledFields.length === 0 &&
					<RCCAlert 
						title={translate("Atenção")}
						description={translate("Nenhum participante encontrado")}
					/>
				}
				{ListParticipantsMemo}
			</RCCGrid>


			<ModalSelectUsers ref={modalSelectUsersRef}  />

		</>
	);
};

export default ParticipantsTab;
